import { useMyPermissions, hasSectionAccess } from './useRoles'
import type { RolePermissions } from './useRoles'

export interface UsePermissionsResult {
  /** Права текущей роли. */
  permissions: RolePermissions | null
  /** Организация помечена как медицинская. */
  isMedical: boolean
  /** Полный доступ (Administrator). */
  isAdmin: boolean
  /** Данные ещё грузятся. */
  isLoading: boolean
  /**
   * Доступ к разделу. Пока права грузятся — разрешаем (флаг isLoading),
   * чтобы не мигать интерфейсом; безопасность обеспечивает бэкенд.
   * Медицинские разделы скрываются для немедицинских организаций.
   */
  can: (section: string, level?: 'view' | 'edit') => boolean
}

/** Разделы, доступные только медицинским организациям (зеркало backend sections). */
const MEDICAL_SECTIONS = new Set([
  'medications',
  'markirovka_stock',
  'markirovka_documents',
  'markirovka_recon',
  'stock_requisitions',
  'assignment_scans',
  'pharma_analytics',
])

export function usePermissions(): UsePermissionsResult {
  const { data, isLoading } = useMyPermissions()

  const permissions = data?.permissions ?? null
  const isMedical = data?.is_medical_organization ?? false
  const isAdmin = permissions?.all === true

  const can = (section: string, level: 'view' | 'edit' = 'view'): boolean => {
    // Медицинские разделы — только для медицинских организаций
    if (MEDICAL_SECTIONS.has(section) && !isMedical) {
      return false
    }
    // Пока грузимся — не прячем интерфейс (бэкенд всё равно проверит)
    if (isLoading || !data) {
      return true
    }
    return hasSectionAccess(permissions, section, level)
  }

  return { permissions, isMedical, isAdmin, isLoading, can }
}
